import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withFormik } from "formik";
import { compose } from "recompose";
import * as yup from "yup";
import cn from "classnames";
import TwoCellRow from "components/TwoCellRow";
import OrderItem from "components/OrderItem";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import Checkbox from "components/Checkbox";
import DownloadButton from "components/DownloadButton";
import { getDocumentsHost } from "common/helper";
import { orderTan } from "api";
import { selectCurrentTenant } from "store/app/selectors";
import { sendFormError } from "i18n/messages/errors";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";
import IntlFormattedMessage from "components/IntlFormattedMessage";

const DOCUMENT_HOST = getDocumentsHost();

const leftClass = "mb-9 d-block";
const rightClass = "text-14 fw-600 d-block mb-9";

const messages = {
    soEditWalletMainHeading: "create_purchase_offer",
    boOTPAcceptMainHeading: "buy_digital_shares",
    boOverviewHeading: "create_purchase_heading",
    soEditWalletHeading: "accept_sales_offer",
    boEditOverviewTileTitle: "overview",
    numberOfDigitalParts: "overview_number_of_digital_shares",
    pricePerShare: "overview_price_per_share",
    totalPurchasePrice: "total_purchase_price",
    purchasePrice: "purchase_price",
    purchaseFee: "purchase_fee",
    boOverviewContent: "understand_all_documents",
    boOverviewsafekeepingCheckboxText: "read_key_information_sheet",
    boOverviewsafekeepingPDFText: "key_information_document",
    boOverviewBondCheckboxText: "noted_terms_conditions",
    boOverviewBondPDFText: "bond_terms_conditions",
    boOverviewCostsCheckboxText: "read_information_costs_benefits",
    boOverviewCostsPDFText: "costs_benefits",
    boOverviewRisksCheckboxText: "noted_risks",
    boOverviewRisksPDFText: "risks",
    boOverviewEffectaCheckboxText: "read_terms_conditions_effecta",
    boOverviewEffectaPDFText: "terms_conditions_effecta_gmbh",
    boOverviewEffectaCICheckboxText: "read_consumer_information",
    boOverviewEffectaCIPDFText: "accept_bo_effecta_cipdf_text",
    boOverviewCreateConfirmCheckboxText: "offered_user_on_secondary_market",
    boOverviewAcceptConfirmCheckboxText: "binding_purchase_of_sales",
    boOTPButtonText: "create_paid_purchase",
    soEditOverviewAcceptButtonText: "buy_now",
    shareShortText: "pc",
};

const BuyOrderOverview = ({
    className = "",
    onBack,
    setCloseTitle,
    values,
    errors,
    touched,
    isOfferAccepted,
    setFieldValue,
    handleSubmit,
    validateForm,
    stepData,
    isSubmitting,
    status,
    backSlideEffect = "",
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    const [orderDocuments, setOrderDocuments] = useState([]);
    const [requestError, setRequestError] = useState(false);

    useEffect(() => {
        setCloseTitle(
            isOfferAccepted ? formatMessage(messages.boOTPAcceptMainHeading) : formatMessage(messages.soEditWalletMainHeading)
        );
    }, []);

    useEffect(() => {
        validateForm();
    }, [values, touched]);

    useEffect(() => {
        stepData?.Document && setOrderDocuments(stepData.Document);
    }, [stepData]);

    useEffect(() => {
        if (status?.requestError) {
            setRequestError(
                formatMessage(
                    status.requestError === "mangopay_beneficiary_account_not_available"
                        ? messages.mangopayBeneficiaryAccountNotAvailable
                        : sendFormError
                )
            );
        }
    }, [status]);

    const isDocumentExist = (fileName) => {
        return orderDocuments?.find((document) => document.Filename.includes(fileName));
    };

    const getDocDownloadPath = (file) => {
        return `${DOCUMENT_HOST}/download${file.Path}${file.Filename}`;
    };

    return (
        <>
            <div className={`buy-order-overview ${className} ${backSlideEffect}`}>
                <h3>
                    {isOfferAccepted ? formatMessage(messages.soEditWalletHeading) : formatMessage(messages.boOverviewHeading)}
                </h3>
                <h4 className="mt-10">{formatMessage(messages.boEditOverviewTileTitle)}</h4>
                <OrderItem
                    equalTitle={false}
                    orderFlag={stepData.flag}
                    tag={stepData.tag}
                    className="mt-0 mb-10 border-active-blue"
                    image={stepData.projectImage}
                    title={stepData.projectTitle}
                    // city={stepData.projectCity}
                    shares={stepData.shares}
                    price={stepData.purchasePrice}
                    date={stepData.expirationDate}
                    perSharePrice={stepData.pricePerShare}
                />
                <div className="overview-details px-3">
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left={formatMessage(messages.numberOfDigitalParts)}
                        right={
                            <>
                                <CustomFormatNumber
                                    value={stepData.shares}
                                    fixed="0"
                                    symbol={formatMessage(messages.shareShortText)}
                                />
                            </>
                        }
                    />
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left={formatMessage(messages.pricePerShare)}
                        right={<CustomFormatNumber value={stepData.pricePerShare} />}
                    />
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left={formatMessage(messages.purchasePrice)}
                        right={<CustomFormatNumber value={stepData.purchasePrice} />}
                    />
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left={`${formatMessage(messages.purchaseFee)} (${stepData.purchaseFeePercent}%):`}
                        right={<CustomFormatNumber value={stepData.purchaseFee} />}
                    />
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left={formatMessage(messages.totalPurchasePrice)}
                        right={<CustomFormatNumber value={stepData.totalPurchasePrice} />}
                    />
                </div>
                <div className="border-x" />
                <div>
                    <p>{formatMessage(messages.boOverviewContent)}</p>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="safekeeping"
                                name="safekeeping"
                                value={values.safekeeping}
                                label={<IntlFormattedMessage id={messages.boOverviewsafekeepingCheckboxText} />}
                                onChange={() => {
                                    setFieldValue("safekeeping", !values.safekeeping);
                                }}
                                isError={touched.safekeeping && errors.safekeeping}
                            />
                        </div>
                        {isDocumentExist("Basisinformationsblatt.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Basisinformationsblatt.pdf`))}
                                title={formatMessage(messages.boOverviewsafekeepingPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="bond"
                                name="bond"
                                value={values.bond}
                                label={<IntlFormattedMessage id={messages.boOverviewBondCheckboxText} />}
                                onChange={() => {
                                    setFieldValue("bond", !values.bond);
                                }}
                                isError={touched.bond && errors.bond}
                            />
                        </div>
                        {isDocumentExist("Schuldverschreibungsbedingungen.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Schuldverschreibungsbedingungen.pdf`))}
                                title={formatMessage(messages.boOverviewBondPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="costs"
                                name="costs"
                                value={values.costs}
                                label={<IntlFormattedMessage id={messages.boOverviewCostsCheckboxText} />}
                                onChange={() => {
                                    setFieldValue("costs", !values.costs);
                                }}
                                isError={touched.costs && errors.costs}
                            />
                        </div>
                        {isDocumentExist("Effecta_Kosten_und_Zuwendungen_Sekundaermarkt.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Effecta_Kosten_und_Zuwendungen_Sekundaermarkt.pdf`))}
                                title={formatMessage(messages.boOverviewCostsPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="risks"
                                name="risks"
                                value={values.risks}
                                label={<IntlFormattedMessage id={messages.boOverviewRisksCheckboxText} />}
                                onChange={() => {
                                    setFieldValue("risks", !values.risks);
                                }}
                                isError={touched.risks && errors.risks}
                            />
                        </div>
                        {isDocumentExist("Risiken.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Risiken.pdf`))}
                                title={formatMessage(messages.boOverviewRisksPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="effecta"
                                name="effecta"
                                value={values.effecta}
                                label={<IntlFormattedMessage id={messages.boOverviewEffectaCheckboxText} />}
                                onChange={() => {
                                    setFieldValue("effecta", !values.effecta);
                                }}
                                isError={touched.effecta && errors.effecta}
                            />
                        </div>
                        {isDocumentExist("Effecta_AGB.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Effecta_AGB.pdf`))}
                                title={formatMessage(messages.boOverviewEffectaPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="effectaCI"
                                name="effectaCI"
                                value={values.effectaCI}
                                label={<IntlFormattedMessage id={messages.boOverviewEffectaCICheckboxText} />}
                                onChange={() => {
                                    setFieldValue("effectaCI", !values.effectaCI);
                                }}
                                isError={touched.effectaCI && errors.effectaCI}
                            />
                        </div>
                        {isDocumentExist("Effecta_Verbraucherinformationen.pdf") && (
                            <DownloadButton
                                isToken
                                className="mb-6 ml-12"
                                fileURL={getDocDownloadPath(isDocumentExist(`Effecta_Verbraucherinformationen.pdf`))}
                                title={formatMessage(messages.boOverviewEffectaCIPDFText)}
                            />
                        )}
                    </div>
                    <div>
                        <div className="check-section d-flex">
                            <Checkbox
                                inputId="confirm"
                                name="confirm"
                                value={values.confirm}
                                label={
                                    isOfferAccepted
                                        ? formatMessage(messages.boOverviewAcceptConfirmCheckboxText)
                                        : formatMessage(messages.boOverviewCreateConfirmCheckboxText)
                                }
                                onChange={() => {
                                    setFieldValue("confirm", !values.confirm);
                                }}
                                isError={touched.confirm && errors.confirm}
                            />
                        </div>
                    </div>
                </div>
                {requestError && <span className="error pl-0">{requestError}</span>}
            </div>
            <div className="bottom-bar-container single-button">
                <PrevButton disable={isSubmitting} onClick={onBack} />
                <NextButton
                    title={
                        isOfferAccepted
                            ? formatMessage(messages.soEditOverviewAcceptButtonText)
                            : formatMessage(messages.boOTPButtonText)
                    }
                    className={cn({
                        "mw-100 mr-1": true,
                        "disable-grey": isSubmitting || Object.keys(errors).length,
                    })}
                    onClick={handleSubmit}
                    disable={isSubmitting || Object.keys(errors).length}
                    isLoading={isSubmitting}
                />
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    tenant: selectCurrentTenant,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        mapPropsToValues: () => {
            return {
                safekeeping: false,
                bond: false,
                costs: false,
                risks: false,
                effecta: false,
                effectaCI: false,
                confirm: false,
            };
        },
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: () => {
            return yup.object().shape({
                safekeeping: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                bond: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                costs: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                // risks: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                effecta: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                effectaCI: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
                confirm: yup.bool().required("checkboxRequired").oneOf([true], "checkboxRequired"),
            });
        },
        handleSubmit: (values, { props, setSubmitting, setStatus }) => {
            try {
                setSubmitting(true);
                // setErrors({ requestError: undefined });

                const orderId = props?.stepData?.uid;

                orderTan(orderId)
                    .then((res) => {
                        res && setSubmitting(false);

                        if (res.data.errors) {
                            const error = res.data.errors[0].message;
                            setStatus({ requestError: error });
                            return;
                        }
                        return props.onForward();
                    })
                    .catch(() => {
                        setStatus({ requestError: true });
                        setSubmitting(false);
                    });
            } catch (error) {
                setStatus({ requestError: true });
                setSubmitting(false);
            }
        },
        displayName: "BuyOrderOverview",
    })
)(BuyOrderOverview);
